import { ethers } from 'ethers'
import { AERO_POOL_FACTORY } from './addresses'
import type { PoolRef, RouteCandidate } from './types'

/**
 * Path encodings, one per venue family. They are separate functions rather
 * than a switch because each is a different on-chain data structure, and the
 * bytes ones are the kind of code that is wrong in a way nothing notices until
 * a transaction reverts — so they are pure and tested against hand-computed
 * hex.
 */

/**
 * Uniswap v3 and Slipstream pack a path as
 * `tokenIn(20) ‖ feeOrSpacing(3) ‖ tokenOut(20)`, extended by 3+20 per extra
 * hop. Uniswap's three bytes are a `uint24 fee`; Slipstream's are an
 * `int24 tickSpacing` — the same three bytes, and every spacing in phase A is
 * positive, so the two never disagree.
 */
export function encodeV3Path(tokens: string[], feesOrSpacings: number[]): string {
  if (tokens.length < 2) throw new Error('encodeV3Path: need at least two tokens')
  if (feesOrSpacings.length !== tokens.length - 1) {
    throw new Error('encodeV3Path: one fee per hop')
  }
  const types: string[] = ['address']
  const values: (string | number)[] = [tokens[0]]
  for (let i = 0; i < feesOrSpacings.length; i++) {
    types.push('uint24', 'address')
    values.push(feesOrSpacings[i], tokens[i + 1])
  }
  return ethers.utils.solidityPack(types, values)
}

/**
 * The `fee` (uni-v3) or `tickSpacing` (Slipstream) each pool of a candidate
 * carries. Both Slipstream factories are named: reading `fee` off a factory-3
 * pool finds nothing and throws, which is how the adapter test caught this.
 */
export function poolFeesOrSpacings(pools: PoolRef[]): number[] {
  return pools.map((p) => {
    const value = p.venue === 'aero-cl' || p.venue === 'aero-cl3' ? p.tickSpacing : p.fee
    if (value == null) throw new Error(`pool ${p.address} has no fee or tick spacing`)
    return value
  })
}

/** The v3-family path bytes for a whole candidate. */
export function candidateV3Path(candidate: RouteCandidate): string {
  return encodeV3Path(candidate.path, poolFeesOrSpacings(candidate.pools))
}

export interface AeroRoute {
  from: string
  to: string
  stable: boolean
  factory: string
}

/**
 * Aerodrome takes routes as structs, and the `factory` field is not optional
 * padding: the router serves several factories, and an empty one sends the
 * swap to whichever it defaults to rather than to the pool that was quoted.
 */
export function aeroRoutes(candidate: RouteCandidate): AeroRoute[] {
  return candidate.pools.map((pool, i) => ({
    from: candidate.path[i],
    to: candidate.path[i + 1],
    stable: Boolean(pool.stable),
    factory: AERO_POOL_FACTORY
  }))
}

/** Uniswap v2 takes the token addresses and finds the pairs itself. */
export function v2Path(candidate: RouteCandidate): string[] {
  return [...candidate.path]
}

/**
 * How many two-hop candidates are ever enumerated.
 *
 * Six hubs across four venues with several pool variants each multiply into
 * hundreds of pairings, nearly all of them empty. The cap keeps the second
 * multicall a fixed size; candidates beyond it are dropped after sorting, so
 * which ones survive is deterministic rather than whatever the map iterated
 * first.
 */
export const MAX_TWO_HOP_CANDIDATES = 64
